// ==================================================
// IMPORTS: React & Hooks
// ==================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
// ==================================================
// IMPORTS: KuddleBoard Styles
// ==================================================
import "../styles/kuddleboard/KuddleBoardLayout.css";
import "../styles/kuddleboard/KuddleBoardLayout.css";
import "../styles/kuddleboard/KuddleBoardHeader.css";
import "../styles/kuddleboard/KuddleBoardCards.css";
import "../styles/kuddleboard/KuddleBoardTables.css";
import "../styles/kuddleboard/KuddleBoardWidgets.css";
import "../styles/kuddleboard/KuddleBoardModals.css";
import "../styles/kuddleboard/KuddleBoardResponsive.css";
// ==================================================
// IMPORTS: Icons
// ==================================================
import {
  Users,
  School,
  CalendarCheck,
  Bell,
  Mail,
  ClipboardList,
  Building2,
  CreditCard,
  ShieldAlert,
} from "lucide-react";
// ==================================================
// IMPORTS: Dashboard Widgets
// ==================================================
import Notepad from "../components/notepad/Notepad";
import LiveChatWidget from "../components/livechat/LiveChatWidget";
import FolderChart from "../components/analytics/FolderChart";
import "../styles/Analytics.css";
import RemindersCard from "../components/Reminders/RemindersCard";
import UserAvatar from "../components/common/UserAvatar";
// ==================================================
// IMPORTS: Context & Navigation
// ==================================================
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
// ==================================================
// IMPORTS: Services
// ==================================================
import api from "../utils/api";
import { io } from "socket.io-client";

// ==================================================
// KUDDLEBOARD PAGE
//
// Purpose:
// Executive operations dashboard for center
// management, staffing, attendance, alerts,
// reminders, communication, and analytics.
//
// ==================================================

const KuddleBoard = () => {

  // ==================================================
  // DASHBOARD STATE
  // ==================================================

  // ----- Messages & Reminders -----
  const [messages, setMessages] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [newReminderId, setNewReminderId] = useState(null);
  // ----- Students & Classrooms -----
  const [studentCount, setStudentCount] = useState(0);
  const [classrooms, setClassrooms] = useState([]);
  // ----- Dashboard Summary -----
  const [todaySummary, setTodaySummary] = useState({
    studentsPresent: 0,
    teachersOnSite: 0,
    roomsOpen: 0,
    attendanceToday: 97,
  });
  // ----- Center Management -----
  const [centerOptions, setCenterOptions] = useState([]);
  const [centerMenuOpen, setCenterMenuOpen] = useState(false);
  const storedUser = JSON.parse(localStorage.getItem("kuddleUser") || "{}");
  const centerSwitcherRef = useRef(null);
  const [activeCenterId, setActiveCenterId] = useState(
    storedUser.currentCenterId || storedUser.primaryCenterId || ""
  );

  // ----- Notifications -----
  const [notifications, setNotifications] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // ----- Birthday Widgets -----
  const [birthdays, setBirthdays] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [birthdayModalTitle, setBirthdayModalTitle] = useState("");
  const [birthdayModalList, setBirthdayModalList] = useState([]);
  // ----- Calendar -----
  const [events, setEvents] = useState([]);
  // ----- Availability Status -----
  const [activeAvailabilityStatus, setActiveAvailabilityStatus] = useState(null);
  const [teamAvailabilityStatuses, setTeamAvailabilityStatuses] = useState([]);
  const [showTeamAvailabilityMenu, setShowTeamAvailabilityMenu] = useState(false);

  // ==================================================
  // ROUTING & AUTH CONTEXT
  // ==================================================
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  // ==================================================
  // HELPER FUNCTIONS
  // ==================================================
  const getCenterId = () => activeCenterId || "";

  const formatStatusTime = (status) => {
    if (status?.expiresAt) {
      return `Until ${new Date(status.expiresAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })}`;
    }

    if (status?.untilTime) return `Until ${status.untilTime}`;
    if (status?.endTime) return `Until ${status.endTime}`;

    return "Active";
  };

  const getBirthdayParts = (value) => {
    if (!value) return null;

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const [year, month, day] = value.slice(0, 10).split("-").map(Number);
      return { year, month: month - 1, day };
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    };
  };

  const formatBirthday = (child) => {
    const value = child.dob || child.dateOfBirth || child.birthDate || child.birthday;
    if (!value) return "Birthday not listed";

    const parts = getBirthdayParts(value);
    if (!parts) return "Birthday not listed";

    return new Date(2026, parts.month, parts.day).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
    });
  };

  // ----- Dashboard Clock -----
  const [dashboardNow, setDashboardNow] = useState(new Date());

  // ==================================================
  // DATA LOADING FUNCTIONS
  // ==================================================
  // ----- Centers -----
  const loadCenters = async () => {
    try {
      const res = await api.get("/api/centers/my-centers");
      const centers = Array.isArray(res.data?.centers) ? res.data.centers : [];

      setCenterOptions(centers);

      if (!activeCenterId && centers.length > 0) {
        handleCenterChange(String(centers[0]._id));
      }
    } catch (err) {
      console.error("Failed to load centers", err);
    }
  };

  const handleCenterChange = (centerId) => {
    const stored = JSON.parse(localStorage.getItem("kuddleUser") || "{}");
    const updatedUser = { ...stored, currentCenterId: centerId };

    localStorage.setItem("kuddleUser", JSON.stringify(updatedUser));

    setActiveCenterId(centerId);
    setCenterMenuOpen(false);

    window.dispatchEvent(
      new CustomEvent("kuddle:center-changed", {
        detail: { centerId },
      })
    );
  };

  // ----- Reminders -----
  const loadReminders = async () => {
    try {
      const res = await api.get("/api/reminders", {
        params: { centerId: getCenterId() },
      });

      const data = Array.isArray(res.data) ? res.data : [];

      if (data.length > 0) {
        setNewReminderId(data[0]._id);
      }

      setReminders(data);
    } catch (err) {
      console.error("Failed to load reminders:", err);
      setReminders([]);
    }
  };

  // ----- Authentication -----
  const handleLogout = async () => {
    try {
      await api.post("/api/sessions/logout-current");
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      logout();
      localStorage.removeItem("selectedAvatar");
      setShowUserMenu(false);
      navigate("/login", { replace: true });
    }
  };

  // ==================================================
  // DASHBOARD EFFECTS
  // ==================================================
  // ----- Live Dashboard Clock -----
  useEffect(() => {
    const timer = setInterval(() => {
      setDashboardNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadCenters();
  }, []);

  // ----- Load Centers -----
  useEffect(() => {
    if (location.state?.refreshCenters) {
      setTimeout(async () => {
        await loadCenters();

        const stored = JSON.parse(localStorage.getItem("kuddleUser") || "{}");
        if (stored.currentCenterId) {
          setActiveCenterId(stored.currentCenterId);
        }
      }, 200);

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // ----- Load Messages -----
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await api.get("/api/analytics/messages", {
          params: { centerId: getCenterId() },
        });

        const list = Array.isArray(res.data) ? res.data : res.data?.messages || [];
        setMessages(list);
      } catch (err) {
        console.error("Failed to load analytics messages:", err);
        setMessages([]);
      }
    };

    loadMessages();
  }, [activeCenterId]);

  // ----- Load Reminders -----
  useEffect(() => {
    loadReminders();

    window.addEventListener("kuddle:reminders-updated", loadReminders);

    return () => {
      window.removeEventListener("kuddle:reminders-updated", loadReminders);
    };
  }, [activeCenterId]);

  useEffect(() => {
    if (!newReminderId) return;

    const timer = setTimeout(() => {
      setNewReminderId(null);
    }, 2000);

    return () => clearTimeout(timer);
  }, [newReminderId]);

  // ----- Load Students -----
  useEffect(() => {
    const loadStudents = async () => {
      try {
        const res = await api.get("/api/students", {
          params: { centerId: getCenterId() },
        });

        const list = Array.isArray(res.data) ? res.data : [];
        setStudentCount(list.length);
      } catch (err) {
        console.error("Students fetch failed:", err);
        setStudentCount(0);
      }
    };

    loadStudents();
  }, [activeCenterId]);

  // ----- Load Events -----
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await api.get("/api/calendar", {
          params: { centerId: getCenterId() },
        });

        setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load calendar events:", err);
        setEvents([]);
      }
    };

    loadEvents();
  }, [activeCenterId]);

  // ----- Load Notifications -----
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const res = await api.get("/api/notifications", {
          params: { centerId: activeCenterId },
        });

        setNotifications(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load notifications:", err);
        setNotifications([]);
      }
    };

    loadNotifications();

    window.addEventListener("kuddle:notifications-updated", loadNotifications);

    return () => {
      window.removeEventListener("kuddle:notifications-updated", loadNotifications);
    };
  }, [activeCenterId]);

  // ----- Notification Socket Listener -----
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL || "");

    socket.on("notifications:new", () => {
      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    });

    return () => socket.disconnect();
  }, []);

  // ----- Load Birthdays -----
  useEffect(() => {
    const loadBirthdays = async () => {
      try {
        const res = await api.get("/api/students", {
          params: { centerId: getCenterId() },
        });

        const students = Array.isArray(res.data) ? res.data : [];
        const today = new Date();
        const currentMonth = today.getMonth();
        const nextMonth = (currentMonth + 1) % 12;

        const list = students.filter((s) => {
          const birthdayValue = s.dob || s.dateOfBirth || s.birthDate || s.birthday;
          if (!birthdayValue) return false;

          const dob = getBirthdayParts(birthdayValue);
          if (!dob) return false;

          return dob.month === currentMonth;
        });

        const upcomingList = students.filter((s) => {
          const birthdayValue = s.dob || s.dateOfBirth || s.birthDate || s.birthday;
          if (!birthdayValue) return false;

          const dob = getBirthdayParts(birthdayValue);
          if (!dob) return false;

          return dob.month === nextMonth;
        });

        setBirthdays(list);
        setUpcomingBirthdays(upcomingList);
      } catch (err) {
        console.error("Birthday load failed:", err);
        setBirthdays([]);
        setUpcomingBirthdays([]);
      }
    };

    loadBirthdays();
  }, [activeCenterId]);

  // ----- Load Team Availability -----
  useEffect(() => {
    const loadTeamAvailabilityStatuses = async () => {
      try {
        const res = await api.get("/api/availability/team", {
          params: { centerId: getCenterId() },
        });

        setTeamAvailabilityStatuses(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load team availability:", err);
        setTeamAvailabilityStatuses([]);
      }
    };

    loadTeamAvailabilityStatuses();

    window.addEventListener("kuddle:availability-updated", loadTeamAvailabilityStatuses);

    return () => {
      window.removeEventListener("kuddle:availability-updated", loadTeamAvailabilityStatuses);
    };
  }, [activeCenterId]);

  // ----- Load Classrooms -----
  useEffect(() => {
    const loadClassrooms = async () => {
      try {
        const res = await api.get("/api/classrooms", {
          params: { centerId: getCenterId() },
        });

        const list = Array.isArray(res.data) ? res.data : [];
        setClassrooms(list);
      } catch (err) {
        console.error("Classrooms fetch failed:", err);
        setClassrooms([]);
      }
    };

    loadClassrooms();
  }, [activeCenterId]);

  // ----- Load Dashboard Summary -----
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const res = await api.get("/api/dashboard/summary", {
          params: { centerId: getCenterId() },
        });

        const data = res.data || {};

        setTodaySummary({
          studentsPresent: data.studentsPresent || 0,
          teachersOnSite: data.teachersOnSite || 0,
          roomsOpen: data.roomsOpen || 0,
          attendanceToday: data.attendanceToday ?? 0,
        });
      } catch (err) {
        console.error("Dashboard summary fetch failed:", err);
      }
    };

    loadSummary();
  }, [activeCenterId]);

  // ----- User Menu Outside Click -----
  useEffect(() => {
    const closeMenu = () => setShowUserMenu(false);
    document.addEventListener("click", closeMenu);

    return () => document.removeEventListener("click", closeMenu);
  }, []);

  // ----- Center Switcher Outside Click -----
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        centerSwitcherRef.current &&
        !centerSwitcherRef.current.contains(event.target)
      ) {
        setCenterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ----- Active Availability Status -----
  useEffect(() => {
    const loadActiveAvailabilityStatus = async () => {
      try {
        const res = await api.get("/api/availability", {
          params: { centerId: getCenterId() },
        });

        const statuses = Array.isArray(res.data) ? res.data : [];

        const activeStatus = statuses.find((item) => {
          if (!item?.enabled) return false;
          if (item.expiresAt && new Date(item.expiresAt) <= new Date()) return false;
          return ["dnd", "focus", "away"].includes(item.type);
        });

        if (!activeStatus) {
          setActiveAvailabilityStatus(null);
          localStorage.removeItem("kuddlecore_active_status");
          return;
        }

        const untilTime = activeStatus.expiresAt
          ? new Date(activeStatus.expiresAt).toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })
          : activeStatus.endTime || "";

        const restoredStatus = {
          id: activeStatus._id,
          type: activeStatus.type,
          text: activeStatus.text || activeStatus.label || "",
          blockLabel: activeStatus.blockLabel,
          label: activeStatus.label,
          icon: activeStatus.icon,
          userName: user?.name || user?.fullName || "Staff",
          untilTime,
          expiresAt: activeStatus.expiresAt,
          bypassRules: activeStatus.bypassRules || {},
        };

        setActiveAvailabilityStatus(restoredStatus);

        localStorage.setItem(
          "kuddlecore_active_status",
          JSON.stringify(restoredStatus)
        );
      } catch (err) {
        console.error("Fetch KuddleBoard availability status failed:", err);
      }
    };

    loadActiveAvailabilityStatus();

    window.addEventListener("storage", loadActiveAvailabilityStatus);
    window.addEventListener("kuddle:availability-updated", loadActiveAvailabilityStatus);

    return () => {
      window.removeEventListener("storage", loadActiveAvailabilityStatus);
      window.removeEventListener("kuddle:availability-updated", loadActiveAvailabilityStatus);
    };
  }, [activeCenterId, user?.name, user?.fullName]);

  // ----- Availability Expiration Timer -----
  useEffect(() => {
    if (!activeAvailabilityStatus?.expiresAt) return;

    const expiresAt = new Date(activeAvailabilityStatus.expiresAt).getTime();
    const timeLeft = expiresAt - Date.now();

    if (timeLeft <= 0) {
      setActiveAvailabilityStatus(null);
      localStorage.removeItem("kuddlecore_active_status");
      window.dispatchEvent(new Event("kuddle:availability-updated"));
      return;
    }

    const timer = setTimeout(() => {
      setActiveAvailabilityStatus(null);
      localStorage.removeItem("kuddlecore_active_status");
      window.dispatchEvent(new Event("kuddle:availability-updated"));
    }, timeLeft);

    return () => clearTimeout(timer);
  }, [activeAvailabilityStatus?.expiresAt]);

  // ==================================================
  // DASHBOARD CALCULATIONS
  // ==================================================
  const unreadNotificationsCount = notifications.filter((n) => n.unread).length;

  const safetyAlertsCount = 0;

  const actionItems = useMemo(
    () => [
      {
        title: "Unpaid Balances",
        value: 0,
        icon: <CreditCard size={18} />,
      },
      {
        title: "Today’s Reminders",
        value: reminders.filter((r) => {
          if (!r.remindAt) return false;
          const d = new Date(r.remindAt);
          const now = new Date();
          return d.toDateString() === now.toDateString();
        }).length,
        icon: <ClipboardList size={18} />,
      },
      {
        title: "Message Activity",
        value: messages.length,
        icon: <Mail size={18} />,
      },
      {
        title: "Safety Alerts",
        value: safetyAlertsCount,
        icon: <ShieldAlert size={18} />,
      },
    ],
    [messages.length, reminders.length, unreadNotificationsCount]
  );

  const upcomingEvents = events
    .filter((e) => new Date(e.eventDate) >= new Date())
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 5);

  const activeCenterName =
    centerOptions.find((center) => String(center._id) === String(activeCenterId))
      ?.centerName || "Select Center";

  // ==================================================
  // RENDER UI
  // ==================================================
  return (
    <div className="kuddleboard-page">
      {/* ----- Dashboard Header ----- */}
      <div className="dashboard-header">
        <div className="dashboard-title-block">
          <h1 className="dashboard-title">KuddleBoard</h1>

          <p className="dashboard-datetime">
            {dashboardNow.toLocaleString()}
          </p>

          <p className="dashboard-subtitle">
            Real-time operations dashboard for staffing, attendance, alerts, and daily activity
          </p>
        </div>

        <div className="dashboard-header-row">
          <div
            className="center-switcher center-switcher-inline"
            ref={centerSwitcherRef}
          >
            <button
              type="button"
              className="center-switcher-btn"
              onClick={() => setCenterMenuOpen((prev) => !prev)}
            >
              <span className="center-switcher-name">{activeCenterName}</span>
              <span className="center-switcher-caret">▾</span>
            </button>

            {centerMenuOpen && (
              <div className="center-switcher-menu">
                {centerOptions.map((center) => (
                  <button
                    key={center._id}
                    type="button"
                    className={`center-switcher-item ${String(center._id) === String(activeCenterId) ? "active" : ""
                      }`}
                    onClick={() => handleCenterChange(String(center._id))}
                  >
                    {center.centerName}
                  </button>
                ))}

                {(user?.role === "owner" || user?.role === "admin") && (
                  <button
                    type="button"
                    className="center-switcher-item center-switcher-add"
                    onClick={() => {
                      setCenterMenuOpen(false);
                      navigate("/centers/new");
                    }}
                  >
                    + Add Center
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="dashboard-actions hero-right">
            <div className="hero-header-actions">
              {teamAvailabilityStatuses.length > 1 ? (
                <div className="kuddleboard-availability-wrap">
                  <button
                    type="button"
                    className="kuddleboard-status-pill"
                    onClick={() => setShowTeamAvailabilityMenu((prev) => !prev)}
                  >
                    {teamAvailabilityStatuses.length} Active Blocks
                  </button>

                  {showTeamAvailabilityMenu && (
                    <div className="kuddleboard-availability-menu">
                      {teamAvailabilityStatuses.map((status) => (
                        <div
                          key={status._id || status.id}
                          className="kuddleboard-availability-item"
                        >
                          <strong>
                            {status.userName ||
                              status.userId?.name ||
                              status.userId?.fullName ||
                              'Staff'}
                          </strong>

                          <span className="kuddleboard-status-reason">
                            {status.blockLabel || status.text || status.label || 'Active Block'}
                          </span>

                          <span className="kuddleboard-status-time">
                            {formatStatusTime(status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                activeAvailabilityStatus && (
                  <div className="kuddleboard-status-pill">
                    {activeAvailabilityStatus.icon || "⛔"}{" "}
                    {activeAvailabilityStatus.userName || user?.name || "Staff"} ·{" "}
                    {activeAvailabilityStatus.blockLabel ||
                      activeAvailabilityStatus.text ||
                      activeAvailabilityStatus.label ||
                      "Active Block"}
                    {activeAvailabilityStatus.untilTime
                      ? ` · ${activeAvailabilityStatus.untilTime}`
                      : ""}
                  </div>
                )
              )}
            </div>

            <button
              type="button"
              className="dashboard-icon-btn"
              title="Notifications"
              onClick={() => navigate("/notifications")}
            >
              <Bell size={20} />

              {unreadNotificationsCount > 0 && (
                <span className="dashboard-icon-badge">
                  {unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount}
                </span>
              )}
            </button>

            <button
              type="button"
              className="dashboard-icon-btn"
              title="Messages"
              onClick={() => navigate("/messages")}
            >
              <Mail size={20} />

              {messages.length > 0 && (
                <span className="dashboard-icon-badge">
                  {messages.length > 9 ? "9+" : messages.length}
                </span>
              )}
            </button>

            <div
              className="hero-user-block"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserMenu((prev) => !prev);
              }}
            >
              <div className="hero-avatar-menu">
                <div className="hero-avatar">
                  <UserAvatar
                    src={user?.avatar}
                    alt={user?.name || user?.fullName || "Staff"}
                    className="hero-avatar-img"
                    size={88}
                  />
                </div>
              </div>

              <div className="hero-user-meta">
                <span className="hero-user-name">
                  {user?.name || user?.fullName || "Staff User"}
                </span>

                <span className="hero-user-role">
                  {user?.role || "Director"}
                </span>
              </div>

              <span className="hero-user-caret">▾</span>

              {showUserMenu && (
                <div className="user-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="user-menu-header">
                    <div className="user-menu-name">
                      {user?.name || user?.fullName || "Staff User"}
                    </div>

                    <div className="user-menu-role">
                      {user?.role || "Director"}
                    </div>

                    <div className="user-menu-center">
                      {activeCenterName}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      navigate("/profile");
                      setShowUserMenu(false);
                    }}
                  >
                    Profile
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      navigate("/settings");
                      setShowUserMenu(false);
                    }}
                  >
                    Settings
                  </button>

                  {(user?.role === "owner" || user?.role === "admin") && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/centers");
                        setShowUserMenu(false);
                      }}
                    >
                      Manage Centers
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={async () => {
                      setShowUserMenu(false);
                      await handleLogout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ----- Summary Cards ----- */}
      <section className="center-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <Users size={18} />
          </div>

          <div className="summary-content">
            <div className="summary-value">{todaySummary.studentsPresent}</div>
            <div className="summary-label">
              Students Present • {studentCount} Enrolled
            </div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <School size={18} />
          </div>

          <div className="summary-content">
            <div className="summary-value">{todaySummary.teachersOnSite}</div>
            <div className="summary-label">Active Staff</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <Building2 size={18} />
          </div>

          <div className="summary-content">
            <div className="summary-value">{todaySummary.roomsOpen}</div>
            <div className="summary-label">Rooms Open</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <CalendarCheck size={18} />
          </div>

          <div className="summary-content">
            <div className="summary-value">{todaySummary.attendanceToday}%</div>
            <div className="summary-label">Attendance Today</div>
          </div>
        </div>
      </section>

      {/* ----- Action Items ----- */}
      <section className="kuddleboard-action-items">
        {actionItems.map((item) => (
          <div
            key={item.title}
            className={`action-item-card ${item.title === "Safety Alerts" ? "clickable" : ""
              }`}
            onClick={() => {
              if (item.title === "Safety Alerts") {
                navigate("/command-center");
              }
            }}
          >
            <div className="action-item-icon">{item.icon}</div>

            <div className="action-item-content">
              <p>{item.title}</p>
              <h3>{item.value}</h3>
            </div>
          </div>
        ))}
      </section>

      {/* ----- Live Room Status ----- */}
      <section className="dashboard-panel">
        <div className="panel-header">
          <div>
            <h2>Live Room Status</h2>
            <p>Quick snapshot of room capacity, staffing, and ratios.</p>
          </div>

          <button
            type="button"
            className="panel-action-btn"
            onClick={() => navigate("/classrooms")}
          >
            View All Rooms
          </button>
        </div>

        <div className="room-table-wrap">
          <table className="room-status-table">
            <thead>
              <tr>
                <th>Room Name</th>
                <th>Capacity</th>
                <th>Students</th>
                <th>Staff</th>
                <th>Current Ratio</th>
                <th>Allowed</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {classrooms.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: "14px", color: "#777" }}>
                    No classroom data available.
                  </td>
                </tr>
              ) : (
                classrooms.map((room) => (
                  <tr key={room._id || room.name}>
                    <td>{room.name}</td>
                    <td>{room.capacity || 0}</td>
                    <td>{room.studentCount || 0}</td>
                    <td>{room.staffCount || 0}</td>

                    <td>
                      {room.ratio?.children >= 0 && room.ratio?.teachers >= 0
                        ? `${room.ratio.children} children / ${room.ratio.teachers
                        } teacher${room.ratio.teachers === 1 ? "" : "s"}`
                        : "—"}
                    </td>

                    <td>{room.allowedChildren ?? 0}</td>

                    <td>
                      {room.overCapacity ? (
                        <span className="room-status-badge critical">
                          Over Capacity
                        </span>
                      ) : room.ratioViolation ? (
                        <span className="room-status-badge critical">
                          Ratio Violation
                        </span>
                      ) : room.ratioWarning ? (
                        <span className="room-status-badge warning">
                          Ratio Warning
                        </span>
                      ) : room.noStaffAssigned ? (
                        <span className="room-status-badge warning">
                          No Staff Assigned
                        </span>
                      ) : (
                        <span className="room-status-badge normal">OK</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ----- Main Dashboard Content ----- */}
      <div className="kuddleboard-main-content">
        {/* ----- Reminder Widget ----- */}
        <div className="kuddleboard-column reminders-column">
          <RemindersCard
            reminders={reminders}
            setReminders={setReminders}
            newReminderId={newReminderId}
          />
        </div>

        {/* ----- Operations Notepad Widget ----- */}
        <div className="kuddleboard-column">
          <div className="panel-header">
            <div>
              <h2>📝 Operations Notepad</h2>
              <p>Keep important center notes visible for staff.</p>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="panel-action-btn"
                onClick={() => navigate("/notepad")}
              >
                View Full Notes
              </button>

              <button
                type="button"
                className="panel-action-btn"
                onClick={() => navigate("/calendar")}
              >
                Open Calendar
              </button>
            </div>
          </div>

          <div className="notepad-preview">
            <Notepad dashboardMode />
          </div>
        </div>

        {/* ----- Analytics Widget ----- */}
        <div className="kuddleboard-column">
          <div className="panel-header">
            <div>
              <h2>Message Analytics</h2>
              <p>Track folder activity and communication volume.</p>
            </div>
          </div>

          <FolderChart messages={messages} compactView />

          {/* ----- Birthday Widget ----- */}
          <div className="birthday-widget">
            <div className="panel-header">
              <div>
                <h2>🎉 Birthdays This Month</h2>
                <p>Celebrate your students today.</p>
              </div>
            </div>

            {birthdays.length > 0 ? (
              <div className="birthday-list">
                {birthdays.slice(0, 3).map((child) => (
                  <div key={child._id} className="birthday-list-item">
                    <span className="birthday-icon">🎂</span>

                    <div>
                      <div className="birthday-name">
                        {child.firstName} {child.lastName}
                      </div>

                      <div className="birthday-date">
                        {formatBirthday(child)}
                      </div>
                    </div>
                  </div>
                ))}

                {birthdays.length > 3 && (
                  <button
                    type="button"
                    className="birthday-more-btn"
                    onClick={() => {
                      setBirthdayModalTitle("Birthdays This Month");
                      setBirthdayModalList(birthdays);
                      setBirthdayModalOpen(true);
                    }}
                  >
                    +{birthdays.length - 3} more birthdays
                  </button>
                )}
              </div>
            ) : (
              <p className="birthday-empty">No birthdays this month.</p>
            )}

            {upcomingBirthdays.length > 0 && (
              <div className="upcoming-birthday-section">
                <h3>Upcoming Next Month</h3>

                <div className="birthday-list">
                  {upcomingBirthdays.slice(0, 2).map((child) => (
                    <div key={child._id} className="birthday-list-item">
                      <span className="birthday-icon">🎈</span>

                      <div>
                        <div className="birthday-name">
                          {child.firstName} {child.lastName}
                        </div>

                        <div className="birthday-date">
                          {formatBirthday(child)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {upcomingBirthdays.length > 2 && (
                    <button
                      type="button"
                      className="birthday-more-btn"
                      onClick={() => {
                        setBirthdayModalTitle("Upcoming Birthdays Next Month");
                        setBirthdayModalList(upcomingBirthdays);
                        setBirthdayModalOpen(true);
                      }}
                    >
                      +{upcomingBirthdays.length - 2} more upcoming
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ----- Birthday Modal ----- */}
      {birthdayModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setBirthdayModalOpen(false)}
        >
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="birthday-modal-header">
              <h3>{birthdayModalTitle}</h3>

              <button
                type="button"
                className="birthday-modal-close"
                onClick={() => setBirthdayModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="birthday-modal-list">
              {birthdayModalList.map((child) => (
                <div key={child._id} className="birthday-list-item">
                  <span className="birthday-icon">🎂</span>

                  <div>
                    <div className="birthday-name">
                      {child.firstName} {child.lastName}
                    </div>

                    <div className="birthday-date">
                      {formatBirthday(child)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ----- Live Chat Widget ----- */}
      <LiveChatWidget />
    </div>
  );
};

export default KuddleBoard;